import { useEffect, useMemo, useRef, useState } from "react";
import { fetchVoiceSentiment } from "./sentimentApi";
import {
  EMPTY_SENTIMENT_PRESENTATION,
  presentationFromSentiment,
  type SentimentPresentation,
} from "./sentimentLogic";
import type { TranscriptTurn } from "./voiceLiveDemoLogic";

const SENTIMENT_DEBOUNCE_MS = 2500;

const ANALYZING_DESCRIPTION =
  "Analyzing customer tone from the live conversation…";

const ANALYSIS_FAILED_DESCRIPTION =
  "Sentiment could not be analyzed for the latest speech.";

export function useVoiceSentiment(transcript: TranscriptTurn[]) {
  const [presentation, setPresentation] = useState<SentimentPresentation>(
    EMPTY_SENTIMENT_PRESENTATION
  );
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const inflightSignatureRef = useRef("");

  const stableTurns = useMemo(
    () =>
      transcript.filter(
        (turn) => !turn.streaming && Boolean(turn.text?.trim())
      ),
    [transcript]
  );

  const hasClaimantSpeech = useMemo(
    () =>
      stableTurns.some(
        (turn) => turn.speaker === "Claimant" && Boolean(turn.text?.trim())
      ),
    [stableTurns]
  );

  const transcriptSignature = useMemo(
    () => stableTurns.map((turn) => `${turn.speaker}:${turn.text}`).join("|"),
    [stableTurns]
  );

  useEffect(() => {
    if (!hasClaimantSpeech) {
      setPresentation(EMPTY_SENTIMENT_PRESENTATION);
      setLoading(false);
      inflightSignatureRef.current = "";
      return;
    }

    if (inflightSignatureRef.current === transcriptSignature) {
      return;
    }

    const timer = window.setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      inflightSignatureRef.current = transcriptSignature;
      setLoading(true);

      try {
        const result = await fetchVoiceSentiment(stableTurns);
        if (requestId !== requestIdRef.current) return;
        setPresentation(presentationFromSentiment(result));
      } catch {
        if (requestId !== requestIdRef.current) return;
        setPresentation({
          tone: "average",
          shortLabel: "Average",
          description: ANALYSIS_FAILED_DESCRIPTION,
          classification: "Neutral",
        });
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          inflightSignatureRef.current = "";
        }
      }
    }, SENTIMENT_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [hasClaimantSpeech, stableTurns, transcriptSignature]);

  const description = loading
    ? ANALYZING_DESCRIPTION
    : presentation.description;

  return {
    presentation,
    loading,
    hasClaimantSpeech,
    isActive: hasClaimantSpeech,
    description,
  };
}
